
import { useEffect, useState } from 'react'
import { disableHardwareBack, goBack, lightImpactVibrate, trackEvent, updateAppStatusBar } from '../../utils/appBridge'
import { EventNames } from '../../types/misc.types'
import BackButton from '../../components/ui-components/BackButton'
import { throttle, toPascalCaseWithSpaces, plural, formatPlanDateRange } from '../../utils/utils'
import MutualFundListGroup from '../../components/power-rebalancing/MutualFundList'
import { powerRebalanceSummary } from '../../repository/rebalance'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { FAQRebalancing } from '../../components/power-rebalancing/FAQRebalancing'
import PeerAnalyserError from '../../components/error-states/PeerAnalyserError'
import { useRiskQuestionnaireStore } from '../../state-management/questionnaireStore'
import { PlanDetailsBottomSheet } from '../../components/power-rebalancing/PlanDetailsBottomSheet'
import { useBottomSheet } from '../../state-management/contexts/BottomSheetContext'
import { getTopPadding } from '../../utils/sessionStorage'
import { useRebalancePlanStore } from '../../state-management/rebalancePlanStore'
import RebalancingDisclaimer from '../../components/power-rebalancing/RebalancingDisclaimer'
import { FamilyRiskProfilePromptSheet } from '../../components/Questionnaire/stopwatch/RiskProfilePromptSheets'

const RebalanceStats = ({
  rebalancedFunds,
  planStartDate,
  planDurationInMonths
}: {
  rebalancedFunds: any[]
  planStartDate: number
  planDurationInMonths: number
}) => {
  const numberOfFundsToRebalance = rebalancedFunds.length
  return (
    <div className='flex flex-col px-5 pb-6'>
      <div className={`text-[22px] font-medium ${numberOfFundsToRebalance === 0 && 'text-status-text-in-form'}`}>
        {numberOfFundsToRebalance === 0 ? 'No' : numberOfFundsToRebalance} {plural('Fund', numberOfFundsToRebalance)} to
        rebalance
      </div>
      <div className='text-grey40 text-[18px]'>{formatPlanDateRange(planStartDate, planDurationInMonths)}</div>
    </div>
  )
}

// Enhanced loading component matching RebalancePlanISIN.tsx
const LoadingSpinner = () => (
  <div className='p-5'>
    <div className='flex flex-col gap-4'>
      <div className='h-[9px] max-w-[72px] bg-grey80 animate-pulse rounded'></div>
      <div className='w-[200px] h-[15px] bg-grey80 animate-pulse rounded'></div>
    </div>
  </div>
)

export const RebalancePlanSummaryPage = () => {
  const { openBottomSheet, closeBottomSheet, destroyBottomSheet } = useBottomSheet()
  const queryClient = useQueryClient()
  const [showSmallHeader, setShowSmallHeader] = useState(false)
  const [searchParams] = useSearchParams()
  const from = searchParams.get('from')
  const navigate = useNavigate()
  const resetQuestionnaire = useRiskQuestionnaireStore((state) => state.resetQuestionnaire)
  const setPlanStartDate = useRebalancePlanStore((state) => state.setPlanStartDate)

  const { data, isLoading, isError } = useQuery({
    queryKey: ['rebalance-plan-summary'],
    queryFn: () => powerRebalanceSummary(),
    retry: 1
  })

  useEffect(() => {
    // This effect can run regardless of the query status
    trackEvent({
      eventName: EventNames.ScreenLoaded,
      payload: {
        screenType: 'REBALANCE_PLAN_PAGE'
      }
    })
    updateAppStatusBar('FFFFFF', 'dark')
    disableHardwareBack()
  }, [])

  const handleBack = () => {
    lightImpactVibrate()
    if (from === 'APP') goBack()
    else if (from === 'first-time-risk-assessment') navigate('/smart-rebalance?eliteMemberShipStatus=ELITE')
    else navigate('/smart-rebalance?eliteMemberShipStatus=ELITE')
  }

  const throttledScroll = throttle((scrollPosition: number) => {
    setShowSmallHeader(scrollPosition >= 70)
  }, 0)

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const scrollPosition = e.currentTarget.scrollTop || 0
    throttledScroll(scrollPosition)
  }

  const handleRankExplanationClick = () => {
    lightImpactVibrate()
    trackEvent({
      eventName: EventNames.UserClicked,
      payload: {
        eventId: 'PlanDetails',
        responseName: 'Expand',
        screenType: 'REBALANCE_PLAN_PAGE'
      }
    })
    openBottomSheet(
      <PlanDetailsBottomSheet
        onDismiss={() => {
          trackEvent({
            eventName: EventNames.UserClicked,
            payload: {
              eventId: 'PlanDetailsBottomSheet',
              responseName: 'Close',
              screenType: 'REBALANCE_PLAN_PAGE'
            }
          })
          closeBottomSheet()
        }}
        ScreenName='REBALANCE_PLAN_PAGE'
      />
    )
  }

  const handleRiskProfileClick = () => {
    trackEvent({
      eventName: EventNames.ButtonTap,
      payload: {
        buttonLabel: 'Risk profile',
        eventId: 'RISK_PROFILE_BUTTON'
      }
    })

    // Check if throughFamilyAccess is true AND familyConsentText exists
    if (throughFamilyAccess && familyConsentText) {
      openBottomSheet(
        <FamilyRiskProfilePromptSheet
          onClose={closeBottomSheet}
          onDestroy={destroyBottomSheet}
          onNavigate={(path) => {
            closeBottomSheet()
            resetQuestionnaire()
            navigate(path)
          }}
          familyMemberName={secondaryUserName ?? ''}
          consentText={familyConsentText}
          familyMemberPhoneNumber={secondaryUserPhoneNumber ?? ''}
          source='rebalance-plan'
        />
      )
    } else {
      // Navigate as is
      resetQuestionnaire()
      navigate('/rebalance-questionnaire?from=rebalance-plan')
    }
  }

  const {
    rebalancedFunds = [],
    fundsExcludedFromRebalancing = [],
    rebalanceExecutedFunds = [],
    riskProfile = '',
    planStartDate,
    planDurationInMonths = 0,
    invalidatedRebalanceFunds = [],
    throughFamilyAccess = false,
    familyConsentText = '',
    secondaryUserName = '',
    secondaryUserPhoneNumber = ''
  } = data?.data || {}

  // Store planStartDate in Zustand store when data is available
  useEffect(() => {
    if (planStartDate) {
      setPlanStartDate(planStartDate)
    }
  }, [planStartDate, setPlanStartDate])

  return (
    <div className='h-[100vh] w-full bg-grey80'>
      <div
        style={{ height: `calc(${getTopPadding()}px + 54px)` }}
        key='small'
        id='fullHeaderSmall'
        className='w-full flex z-[99] bg-white'
      >
        <div
          style={{ paddingTop: `calc(${getTopPadding()}px + 16px)` }}
          className={`flex items-center relative w-full pb-5 pr-5 pl-[19px] h-full bg-white border-b ${showSmallHeader ? 'border-[#EBF2F2]' : 'border-white'} `}
        >
          <div className='flex absolute z-1'>
            <BackButton size={24} onClick={handleBack} />
          </div>
          <div className='w-full flex justify-start ml-10 items-center'>
            {showSmallHeader && (
              <div className='max-w-[70%] animate-fade-150 truncate font-[500] mr-4'>
                {rebalancedFunds.length} {plural('fund', rebalancedFunds.length)} to rebalance
              </div>
            )}
          </div>
        </div>
      </div>
      <div
        onScroll={handleScroll}
        className={`h-[calc(100vh-60px)] w-full flex flex-col flex-1 overflow-y-scroll no-scrollbar overflow-x-hidden overscroll-none ${isLoading ? 'bg-white' : 'bg-white'}`}
      >
        {isLoading ? (
          <LoadingSpinner />
        ) : isError || !data?.data ? (
          <div className='flex items-center h-[calc(100vh-80px)] relative mb-[20px] mx-5'>
            <PeerAnalyserError
              onRetry={() => {
                queryClient.invalidateQueries({ queryKey: ['rebalance-plan-summary'] })
              }}
            />
          </div>
        ) : (
          <>
            <RebalanceStats
              rebalancedFunds={rebalancedFunds}
              planStartDate={planStartDate}
              planDurationInMonths={planDurationInMonths}
            />
            <MutualFundListGroup
              invalidatedRebalanceFunds={invalidatedRebalanceFunds}
              rebalanceExecutedFunds={rebalanceExecutedFunds}
              rebalancedFunds={rebalancedFunds}
              planStartDate={planStartDate}
              fundsExcludedFromRebalancing={fundsExcludedFromRebalancing}
              navigate={navigate}
              riskProfile={riskProfile}
              throughFamilyAccess={throughFamilyAccess}
              secondaryUserName={secondaryUserName}
            />
            <div className='bg-grey80'>
              <div className='flex flex-col px-5 pb-6 pt-8 gap-2'>
                <div onClick={handleRankExplanationClick} className='text-13 text-grey40 underline'>
                  What does your plan include and exclude?
                </div>
                <div className='text-sm text-grey40 mb-1'>
                  Based on your risk profile:{' '}
                  <span onClick={handleRiskProfileClick} className='underline cursor-pointer'>
                    {toPascalCaseWithSpaces(riskProfile)}
                  </span>
                </div>
              </div>
            </div>
            <div className='bg-grey80'>
              <FAQRebalancing faqHeader='FAQ' />
            </div>
            <RebalancingDisclaimer />
          </>
        )}
      </div>
    </div>
  )
}
